/**
 * ChatCardRenderer
 *
 * Renders a single ChatCard returned by an AI tool.
 * Handles gmail_message, gmail_draft, gmail_sent, calendar_event,
 * action_prompt, and a generic fallback for unknown types.
 */

import { Mail, Calendar, ExternalLink, Clock, User } from 'lucide-react';
import clsx from 'clsx';
import type { ChatCard } from '../types';

interface Props {
  card: ChatCard;
  onAction?: (action: NonNullable<ChatCard['actions']>[number]) => void;
}

export function ChatCardRenderer({ card, onAction }: Props) {
  switch (card.type) {
    case 'gmail_message':
      return <GmailMessageCard card={card} onAction={onAction} />;
    case 'gmail_draft':
      return <GmailDraftCard card={card} onAction={onAction} />;
    case 'gmail_sent':
      return <GmailSentCard card={card} onAction={onAction} />;
    case 'calendar_event':
      return <CalendarEventCard card={card} onAction={onAction} />;
    case 'action_prompt':
      return <ActionPromptCard card={card} onAction={onAction} />;
    default:
      return <GenericCard card={card} onAction={onAction} />;
  }
}

// ─── Card wrappers ─────────────────────────────────────────────────────────────

const cardBase =
  'mt-2 pt-2 border-t border-border-subtle-light/20 dark:border-border-hover-dark/20';

function ActionButtons({
  actions,
  onAction,
}: {
  actions: NonNullable<ChatCard['actions']>;
  onAction?: (action: NonNullable<ChatCard['actions']>[number]) => void;
}) {
  if (!actions.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {actions.map((action, i) => (
        <button
          key={i}
          onClick={() => onAction?.(action)}
          className="px-3 py-1 text-xs rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

// ─── Gmail message card ────────────────────────────────────────────────────────

function GmailMessageCard({ card, onAction }: Props) {
  const meta = card.metadata as {
    from?: string;
    snippet?: string;
    threadId?: string;
    isUnread?: boolean;
  };

  return (
    <div className={cardBase}>
      <div className="flex items-start gap-2">
        <Mail className="w-3.5 h-3.5 mt-0.5 text-text-secondary-light dark:text-text-secondary-dark shrink-0" />
        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              'text-xs leading-snug',
              meta.isUnread
                ? 'font-semibold text-text-primary-light dark:text-text-primary-dark'
                : 'font-medium text-text-primary-light dark:text-text-primary-dark',
            )}
          >
            {card.title}
          </p>
          {card.subtitle && (
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
              {card.subtitle}
            </p>
          )}
          {meta.snippet && (
            <p className="text-xs text-text-secondary-light/80 dark:text-text-secondary-dark/80 mt-0.5 line-clamp-2">
              {meta.snippet}
            </p>
          )}
          {card.url && (
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
            >
              Open in Gmail <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {card.actions && card.actions.length > 0 && (
            <ActionButtons actions={card.actions} onAction={onAction} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Gmail draft card ──────────────────────────────────────────────────────────

function GmailDraftCard({ card, onAction }: Props) {
  return (
    <div className={cardBase}>
      <div className="flex items-start gap-2">
        <Mail className="w-3.5 h-3.5 mt-0.5 text-amber-500 dark:text-amber-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-text-primary-light dark:text-text-primary-dark">
            Draft: {card.title}
          </p>
          {card.subtitle && (
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
              {card.subtitle}
            </p>
          )}
          {card.url && (
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
            >
              Open in Gmail <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {card.actions && card.actions.length > 0 && (
            <ActionButtons actions={card.actions} onAction={onAction} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Gmail sent card ───────────────────────────────────────────────────────────

function GmailSentCard({ card, onAction }: Props) {
  return (
    <div className={cardBase}>
      <div className="flex items-start gap-2">
        <Mail className="w-3.5 h-3.5 mt-0.5 text-green-500 dark:text-green-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-text-primary-light dark:text-text-primary-dark">
            {card.title}
          </p>
          {card.subtitle && (
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
              {card.subtitle}
            </p>
          )}
          {card.url && (
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
            >
              View sent mail <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {card.actions && card.actions.length > 0 && (
            <ActionButtons actions={card.actions} onAction={onAction} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Calendar event card ───────────────────────────────────────────────────────

function CalendarEventCard({ card, onAction }: Props) {
  const meta = card.metadata as {
    start?: string;
    end?: string;
    calendarName?: string;
    isDeclined?: boolean;
  };

  return (
    <div className={cardBase}>
      <div className="flex items-start gap-2">
        <Calendar
          className={clsx(
            'w-3.5 h-3.5 mt-0.5 shrink-0',
            meta.isDeclined
              ? 'text-text-secondary-light dark:text-text-secondary-dark'
              : 'text-primary-600 dark:text-primary-400',
          )}
        />
        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              'text-xs font-medium leading-snug',
              meta.isDeclined
                ? 'text-text-secondary-light dark:text-text-secondary-dark line-through'
                : 'text-text-primary-light dark:text-text-primary-dark',
            )}
          >
            {card.title}
          </p>
          {card.subtitle && (
            <div className="flex items-center gap-1 mt-0.5">
              <Clock className="w-3 h-3 text-text-secondary-light dark:text-text-secondary-dark shrink-0" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                {card.subtitle}
              </p>
            </div>
          )}
          {meta.calendarName && (
            <div className="flex items-center gap-1 mt-0.5">
              <User className="w-3 h-3 text-text-secondary-light dark:text-text-secondary-dark shrink-0" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                {meta.calendarName}
              </p>
            </div>
          )}
          {card.url && (
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
            >
              Open in Google Calendar <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {card.actions && card.actions.length > 0 && (
            <ActionButtons actions={card.actions} onAction={onAction} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Action prompt card (existing behavior) ────────────────────────────────────

function ActionPromptCard({ card, onAction }: Props) {
  return (
    <div className={cardBase}>
      <p className="text-xs font-medium text-text-primary-light dark:text-text-primary-dark">
        {card.title}
      </p>
      {card.subtitle && (
        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
          {card.subtitle}
        </p>
      )}
      {card.actions && card.actions.length > 0 && (
        <ActionButtons actions={card.actions} onAction={onAction} />
      )}
    </div>
  );
}

// ─── Generic fallback card ─────────────────────────────────────────────────────

function GenericCard({ card, onAction }: Props) {
  return (
    <div className={cardBase}>
      <p className="text-xs font-medium text-text-primary-light dark:text-text-primary-dark">
        {card.title}
      </p>
      {card.subtitle && (
        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
          {card.subtitle}
        </p>
      )}
      {card.url && (
        <a
          href={card.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
        >
          Open <ExternalLink className="w-3 h-3" />
        </a>
      )}
      {card.actions && card.actions.length > 0 && (
        <ActionButtons actions={card.actions} onAction={onAction} />
      )}
    </div>
  );
}
